import React, { useRef, useState, useLayoutEffect, useMemo } from 'react';
import { ApiTemplateObject, LayoutTemplateDef } from '@/lib/parseTemplateObjects';
import { StickerInfosByStyle, CoverInfosByStyle, AllFolder } from '@/hooks/useBookLoader';
import {
  Lightbulb,
  Users,
  ImageIcon,
  LayoutGrid,
  Paintbrush,
  Type,
  Smile,
  BookOpen,
  QrCode,
  Grid3x3,
} from 'lucide-react';
import { ToolType, YearbookElement, YearbookPage } from '@/types/yearbook';
import { TrameTemplateInfosByStyle, TrameBackgroundInfosByStyle } from '@/hooks/useBookLoader';
import InspirationPanel from './sidebar/InspirationPanel';
import TrombiPanel from './sidebar/TrombiPanel';
import PhotosPanel from './sidebar/PhotosPanel';
import LayoutPanel from './sidebar/LayoutPanel';
import BackgroundPanel from './sidebar/BackgroundPanel';
import TextPanel from './sidebar/TextPanel';
import StickersPanel from './sidebar/StickersPanel';
import CoverPanel from './sidebar/CoverPanel';
import QrCodePanel from './sidebar/QrCodePanel';
import CrosswordPanel from './sidebar/CrosswordPanel';


interface SidebarProps {
  activeTool: ToolType;
  setActiveTool: (tool: ToolType) => void;
  isCoverPage?: boolean;
  isTranchePage?: boolean;
  onAddText: (variant?: 'title' | 'subtitle' | 'text', customContent?: string) => void;
  onAddImage: (url: string, ratio?: number, pictureId?: number, thumbUrl?: string) => void;
  onAddSticker: (imageUrl: string, width: number, height: number, opts?: { format?: 'png' | 'svg' | 'simple_svg'; defaultColor?: string }) => void;
  onAddQrCode: (imageUrl: string, width: number, height: number, qrcodeId: number) => void;
  onAddElements: (elements: YearbookElement[]) => void;
  onAddShape: (shapeType: 'rect' | 'circle' | 'line', color?: string) => void;
  onSetBackground: (type: 'color' | 'image', value: string, backgroundTemplateId?: number, isCustom?: boolean) => void;
  onSetRichBackground: (backgroundId: number, imageUrl: string, objects: ApiTemplateObject[]) => void;
  activeBackgroundId?: number | null;
  activeBackgroundCustomId?: number | null;
  onApplyLayout: (layout: LayoutTemplateDef, templateId?: number) => void;
  onApplyCover: (data: { elements_front: any[]; elements_back: any[]; cover_name?: string }) => void;
  activeTemplateId?: number | null;
  selectedElement: YearbookElement | null;
  selectedElements?: YearbookElement[];
  onUpdateElement: (id: string, updates: Partial<YearbookElement>) => void;
  trameTemplateInfos: TrameTemplateInfosByStyle;
  trameBackgroundInfos: TrameBackgroundInfosByStyle;
  initialFavoritesColors: string[];
  onFavColorsChange: (colors: string[]) => void;
  pages: YearbookPage[];
  pictureToUse: Set<number>;
  stickerInfos: StickerInfosByStyle;
  coverInfos: CoverInfosByStyle;
  onTrombiCreated: (data: Record<string, { objects_positions: any[] }>) => void;
  onTrombiRemoved: (pageIds: number[]) => void;
  onTrombiUpdated: (data: Record<string, { objects_positions: any[] }>) => void;
  onApplyMagicPage: (pageIndex: number, elements: YearbookElement[], background?: import('@/types/yearbook').PageBackground, layoutTemplateId?: number) => void;
  onNavigateToPage: (pageIndex: number) => void;
  allFolders: AllFolder[];
  autoOpenPhotoImport?: boolean;
  withTrombi?: boolean;
  isWememberz?: boolean;
  autoApplyDefaultCover?: boolean;
}

const allTabs: { id: ToolType; icon: React.ReactNode; label: string; coverOnly?: boolean; pageOnly?: boolean }[] = [
  { id: 'inspiration', icon: <Lightbulb size={22} />, label: 'Inspiration' },
  { id: 'trombi', icon: <Users size={22} />, label: 'Trombi' },
  { id: 'photos', icon: <ImageIcon size={22} />, label: 'Photos' },
  { id: 'layout', icon: <LayoutGrid size={22} />, label: 'Modèles', pageOnly: true },
  { id: 'cover', icon: <BookOpen size={22} />, label: 'Couverture', coverOnly: true },
  { id: 'background', icon: <Paintbrush size={22} />, label: 'Fonds', pageOnly: true },
  { id: 'text', icon: <Type size={22} />, label: 'Texte' },
  { id: 'stickers', icon: <Smile size={22} />, label: 'Stickers' },
  { id: 'qrcode', icon: <QrCode size={22} />, label: 'QR Code' },
  { id: 'crossword', icon: <Grid3x3 size={22} />, label: 'Mots croisés', pageOnly: true },
];

const Sidebar: React.FC<SidebarProps> = ({
  activeTool,
  setActiveTool,
  isCoverPage,
  isTranchePage,
  onAddText,
  onAddImage,
  onAddSticker,
  onAddQrCode,
  onAddElements,
  onAddShape,
  onSetBackground,
  onSetRichBackground,
  onApplyLayout,
  onApplyCover,
  activeTemplateId,
  activeBackgroundId,
  activeBackgroundCustomId,
  selectedElement,
  onUpdateElement,
  trameTemplateInfos,
  trameBackgroundInfos,
  initialFavoritesColors,
  onFavColorsChange,
  pages,
  pictureToUse,
  stickerInfos,
  coverInfos,
  onTrombiCreated,
  onTrombiRemoved,
  onTrombiUpdated,
  onApplyMagicPage,
  onNavigateToPage,
  allFolders,
  autoOpenPhotoImport = false,
  withTrombi = true,
  isWememberz = false,
  autoApplyDefaultCover = false,
}) => {
  const tabs = useMemo(() => allTabs.filter((tab) => {
    // On tranche page: only allow the Text tab.
    if (isTranchePage) return tab.id === 'text';
    if (tab.coverOnly && !isCoverPage) return false;
    if (tab.pageOnly && isCoverPage) return false;
    if (tab.id === 'trombi' && !withTrombi) return false;
    return true;
  }), [isCoverPage, isTranchePage, withTrombi]);

  // Sliding indicator
  const railRef = useRef<HTMLDivElement>(null);
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicator, setIndicator] = useState({ top: 0, height: 0 });

  useLayoutEffect(() => {
    const idx = tabs.findIndex((t) => t.id === activeTool);
    const btn = btnRefs.current[idx];
    const rail = railRef.current;
    if (btn && rail) {
      const rRect = rail.getBoundingClientRect();
      const bRect = btn.getBoundingClientRect();
      setIndicator({ top: bRect.top - rRect.top, height: bRect.height });
    }
  }, [activeTool, tabs]);

  // Lazy-mount: keep track of which panels have ever been opened so they
  // mount only on first activation (and stay mounted afterwards to preserve state).
  const [mountedPanels, setMountedPanels] = useState<Set<ToolType>>(() => new Set([activeTool]));
  React.useEffect(() => {
    setMountedPanels((prev) => {
      if (prev.has(activeTool)) return prev;
      const next = new Set(prev);
      next.add(activeTool);
      return next;
    });
  }, [activeTool]);
  const isMounted = (id: ToolType) => mountedPanels.has(id);

  return (
    <div className="flex h-full">
      {/* Icon rail – vertically centered */}
      <div
        ref={railRef}
        className="w-[68px] bg-editor-sidebar flex flex-col items-center justify-center gap-0.5 border-r border-editor-sidebar-border shrink-0 relative"
      >
        {/* Sliding active background */}
        <div
          className="absolute left-1 right-1 rounded-xl bg-editor-sidebar-hover transition-all duration-300 ease-out pointer-events-none"
          style={{ top: indicator.top, height: indicator.height }}
        />
        {/* Active accent bar on the left edge */}
        <div
          className="absolute left-0 w-[3px] rounded-r-full bg-primary-gradient transition-all duration-300 ease-out pointer-events-none"
          style={{ top: indicator.top + 8, height: Math.max(0, indicator.height - 16) }}
        />

        {tabs.map((tab, i) => {
          const isActive = activeTool === tab.id;
          return (
            <button
              key={tab.id}
              ref={(el) => { btnRefs.current[i] = el; }}
              onClick={() => setActiveTool(tab.id)}
              className={`relative z-10 w-[64px] flex flex-col items-center gap-1 py-3 px-1 rounded-xl text-[10px] transition-all duration-200 ${
                isActive
                  ? 'text-editor-sidebar-active'
                  : 'text-editor-sidebar-foreground/80 hover:text-editor-sidebar-foreground'
              }`}
            >
              <span className={`transition-transform duration-200 ${isActive ? 'scale-110' : ''}`}>
                {tab.icon}
              </span>
              <span className={`leading-tight text-center transition-opacity duration-200 ${isActive ? 'opacity-100 font-medium' : 'opacity-70'}`}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Content panel */}
      <div className="w-60 bg-editor-sidebar flex flex-col h-full overflow-hidden">
        <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${activeTool === 'inspiration' ? '' : 'hidden'}`}>
          {isMounted('inspiration') && (
            <InspirationPanel pages={pages} onApplyMagicPage={onApplyMagicPage} onNavigateToPage={onNavigateToPage} isWememberz={isWememberz} />
          )}
        </div>
        <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${activeTool === 'trombi' ? '' : 'hidden'}`}>
          {isMounted('trombi') && (
            <TrombiPanel pages={pages} onTrombiCreated={onTrombiCreated} onTrombiRemoved={onTrombiRemoved} onTrombiUpdated={onTrombiUpdated} favColors={initialFavoritesColors} />
          )}
        </div>
        <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${activeTool === 'photos' ? '' : 'hidden'}`}>
          {isMounted('photos') && (
            <PhotosPanel onAddImage={onAddImage} onAddText={onAddText} pictureToUse={pictureToUse} allFolders={allFolders} isActive={activeTool === 'photos'} autoOpenImport={autoOpenPhotoImport} isWememberz={isWememberz} />
          )}
        </div>
        {!isCoverPage && (
          <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${activeTool === 'layout' ? '' : 'hidden'}`}>
            {isMounted('layout') && (
              <LayoutPanel onApplyLayout={onApplyLayout} activeTemplateId={activeTemplateId} trameTemplateInfos={trameTemplateInfos} isWememberz={isWememberz} />
            )}
          </div>
        )}
        {isCoverPage && (
          <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${activeTool === 'cover' ? '' : 'hidden'}`}>
            {isMounted('cover') && (
              <CoverPanel onApplyCover={onApplyCover} coverInfos={coverInfos} isWememberz={isWememberz} autoApplyDefault={autoApplyDefaultCover} />
            )}
          </div>
        )}
        <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${activeTool === 'background' ? '' : 'hidden'}`}>
          {isMounted('background') && (
            <BackgroundPanel onSetBackground={onSetBackground} onSetRichBackground={onSetRichBackground} activeBackgroundId={activeBackgroundId} activeBackgroundCustomId={activeBackgroundCustomId} trameBackgroundInfos={trameBackgroundInfos} favColors={initialFavoritesColors} onFavColorsChange={onFavColorsChange} isWememberz={isWememberz} />
          )}
        </div>
        <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${activeTool === 'text' ? '' : 'hidden'}`}>
          {isMounted('text') && (
            <TextPanel
              onAddText={onAddText}
              selectedElement={selectedElement}
              onUpdateElement={onUpdateElement}
              favColors={initialFavoritesColors}
              onFavColorsChange={onFavColorsChange}
            />
          )}
        </div>
        <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${activeTool === 'stickers' ? '' : 'hidden'}`}>
          {isMounted('stickers') && (
            <StickersPanel onAddSticker={onAddSticker} onAddShape={onAddShape} stickerInfos={stickerInfos} selectedElement={selectedElement} onUpdateElement={onUpdateElement} favColors={initialFavoritesColors} onFavColorsChange={onFavColorsChange} />
          )}
        </div>
        <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${activeTool === 'qrcode' ? '' : 'hidden'}`}>
          {isMounted('qrcode') && (
            <QrCodePanel onAddQrCode={onAddQrCode} initialFavoritesColors={initialFavoritesColors} />
          )}
        </div>
        <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${activeTool === 'crossword' ? '' : 'hidden'}`}>
          {isMounted('crossword') && (
            <CrosswordPanel onAddElements={onAddElements} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;